/**
 * Responsibility: Implement game event artifact behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import { z } from 'zod'
import type { GameEventChoice, GameEventState } from "@anyquest/shared-core"

const EVENT_IMAGE_KINDS = ['item', 'npc', 'scene', 'companion'] as const
const MAX_EVENT_CHOICES = 6

const GameEventChoiceSchema = z.object({
  label: z.string().trim().min(1).max(80),
  input: z.string().trim().min(1).max(160),
})

const GameEventStateSchema = z.object({
  id: z.string().trim().min(1).max(80),
  title: z.string().trim().min(1).max(120),
  desc: z.string().trim().min(1).max(1200),
  imageKind: z.enum(EVENT_IMAGE_KINDS).optional(),
  imageSeed: z.string().trim().min(1).max(160).optional(),
  choices: z.array(GameEventChoiceSchema).min(1).max(MAX_EVENT_CHOICES),
  resolved: z.boolean().optional(),
})

type GameEventStateInput = z.input<typeof GameEventStateSchema>
type GameEventChoiceInput = z.input<typeof GameEventChoiceSchema>

function cloneChoice(choice: GameEventChoiceInput): GameEventChoice {
  return {
    label: choice.label.trim(),
    input: choice.input.trim(),
  }
}

function cloneEvent(event: GameEventStateInput): GameEventState {
  return {
    id: event.id.trim(),
    title: event.title.trim(),
    desc: event.desc.trim(),
    ...(event.imageKind ? { imageKind: event.imageKind } : {}),
    ...(event.imageSeed ? { imageSeed: event.imageSeed.trim() } : {}),
    choices: event.choices.map(cloneChoice),
    ...(event.resolved ? { resolved: true } : {}),
  }
}

export class GameEventArtifact {
  private readonly dto: GameEventState

  private constructor(dto: GameEventState) {
    this.dto = dto
  }

  static fromUnknown(input: unknown): GameEventArtifact | null {
    const parsed = GameEventStateSchema.safeParse(input)
    if (!parsed.success) return null
    return new GameEventArtifact(cloneEvent(parsed.data))
  }

  static fromDTO(dto: GameEventState): GameEventArtifact {
    const parsed = GameEventStateSchema.parse(dto)
    return new GameEventArtifact(cloneEvent(parsed))
  }

  get id(): string {
    return this.dto.id
  }

  get resolved(): boolean {
    return this.dto.resolved === true
  }

  markResolved(): void {
    this.dto.resolved = true
  }

  toDTO(): GameEventState {
    return cloneEvent(this.dto)
  }
}

export function parseGameEventState(input: unknown): GameEventState | null {
  return GameEventArtifact.fromUnknown(input)?.toDTO() ?? null
}
